import React from 'react';
import { 
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { ChevronDown, Building2 } from 'lucide-react';
import { Promotion } from '@/client/types/promotion';
import { usePausePromotionMutation, useResumePromotionMutation } from '@/client/api/promotion-management/promotion.mutation';
import { toast } from 'sonner';

interface ServiceRequestDetailsDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  promotion: Promotion | null;
  onClose: () => void;
}

const getProgressColor = (value: number, target: number) => {
  if (!target) return 'bg-gray-200';
  const percentage = (value / target) * 100;
  if (percentage >= 90) return 'bg-green-500';
  if (percentage >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getPromotionStatus = (p: Promotion): Promotion['status'] => {
  if (p.status) return p.status;
  if (p.canceled) return 'canceled';
  if (p.expired) return 'expired';
  if (p.active) return 'active';
  if (p.date_paused) return 'paused';
  return 'inactive';
};

const ServiceRequestDetailsDrawer: React.FC<ServiceRequestDetailsDrawerProps> = ({
  isOpen,
  setIsOpen,
  promotion,
  onClose
}) => {
  const pauseMutation = usePausePromotionMutation();
  const resumeMutation = useResumePromotionMutation();

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      draft: 'bg-slate-100 text-slate-800',
      expired: 'bg-gray-200 text-gray-800',
      canceled: 'bg-red-100 text-red-800',
      inactive: 'bg-neutral-100 text-neutral-800',
    };
    return styles[status as keyof typeof styles] || styles.inactive;
  };

  if (!promotion) return null;

  const status = getPromotionStatus(promotion);
  const isPaused = status === 'paused';
  const isActive = status === 'active';

  const handlePause = async () => {
    if (!promotion?.promid || pauseMutation.isPending) return;
    try {
      await pauseMutation.mutateAsync({ promid: promotion.promid });
      toast.success('Promotion paused successfully', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to pause promotion', { richColors: true });
    }
  };

  const handleResume = async () => {
    if (!promotion?.promid || resumeMutation.isPending) return;
    try {
      await resumeMutation.mutateAsync({ promid: promotion.promid });
      toast.success('Promotion resumed successfully', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to resume promotion', { richColors: true });
    }
  };

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="h-full top-0 right-0 left-auto mt-0 w-full max-w-[500px] rounded-none [&>div.bg-muted]:hidden">
        <div className="mx-auto w-full h-full flex flex-col bg-white">
          <DrawerHeader className="border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <DrawerTitle className="text-lg font-bold text-gray-900 text-left">
                    Promotion Details
                  </DrawerTitle>
                  <DrawerDescription className="mt-1 text-left">
                    Manage and view promotion details
                  </DrawerDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <ChevronDown className="h-6 w-6" />
              </Button>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Basic Information</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500">Title</label>
                  <p className="text-sm font-medium text-gray-900">{promotion.name}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Organization</label>
                  <p className="text-sm font-medium text-gray-900">OID: {promotion.oid || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Promotion Type</label>
                  <p className="text-sm font-medium text-gray-900">{promotion.description}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(getPromotionStatus(promotion))}`}>
                    {getPromotionStatus(promotion).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Performance Metrics</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-xs text-gray-500">Views</label>
                  <p className="text-lg font-medium text-gray-900">{promotion.views.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-xs text-gray-500">Impressions</label>
                  <p className="text-lg font-medium text-gray-900">{promotion.impressions.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <label className="block text-xs text-gray-500">CTR</label>
                  <p className="text-lg font-medium text-gray-900">{((promotion.views / (promotion.impressions || 1)) * 100).toFixed(2)}%</p>
                </div>
              </div>
            </div>

            {/* Goal Progress */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Goal Progress</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">
                    {promotion.impressions} of {promotion.impression_target} completed
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {((promotion.impressions / (promotion.impression_target || 1)) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${getProgressColor(promotion.impressions, promotion.impression_target)} h-2 rounded-full`}
                    style={{
                      width: `${Math.min((promotion.impressions / (promotion.impression_target || 1)) * 100, 100)}%`
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Budget Information - Placeholder */}
            <div>
               <h4 className="text-sm font-medium text-gray-900 mb-2">Budget</h4>
               <p className="text-sm text-gray-500">Budget information not available</p>
            </div>
          </div>

          <DrawerFooter className="border-t bg-gray-50">
            <div className="flex justify-end space-x-3 w-full">
              <DrawerClose asChild>
                <Button variant="outline" onClick={onClose}>Close</Button>
              </DrawerClose>
              {isActive && (
                <Button
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  disabled={pauseMutation.isPending}
                  onClick={handlePause}
                >
                  {pauseMutation.isPending ? 'Pausing...' : 'Pause Promotion'}
                </Button>
              )}
              {isPaused && (
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={resumeMutation.isPending}
                  onClick={handleResume}
                >
                  {resumeMutation.isPending ? 'Resuming...' : 'Resume Promotion'}
                </Button>
              )}
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ServiceRequestDetailsDrawer;
